import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSOperators = () => {
    const basicOpCode = `// Arithmetic & Modern Assignments
let score = 10;
score += 5;        // 15
score **= 2;       // 225 (Exponentiation)

// Modulus: Critical for cycling and parity
const isEven = (n) => n % 2 === 0;
console.log(10 % 3); // 1 (Remainder)`;

    const comparisonCode = `// Strict vs Loose Comparison
console.log(5 === '5');  // false (Strict - checks type)
console.log(5 == '5');   // true (Loose - triggers coercion)

// Relational operators with strings (Lexicographical)
console.log('apple' < 'banana'); // true
console.log('2' < '10');        // false (String '2' is greater than '1')`;

    const logicalShortCircuitCode = `// Short-Circuit Evaluation
// && returns first falsy or last truthy
const user = { name: 'Alice' };
const displayName = user && user.name; // 'Alice'

// || returns first truthy or last falsy (Useful for defaults)
const theme = userTheme || 'dark';

// Logical Assignment (ES2021)
let x = 0;
x ||= 10;  // 10 (because 0 is falsy)
x &&= 20;  // 20 (because 10 is truthy)
x ??= 30;  // 20 (because 20 is not null/undefined)`;

    const nullishCode = `// Nullish Coalescing (??) vs Logical OR (||)
const count = 0;

const val1 = count || 10; // 10 (Problem: 0 is falsy)
const val2 = count ?? 10; // 0  (Correct: 0 is not null/undefined)

// Use ?? for settings where 0 or "" are valid entries.`;

    const bitwiseCode = `// Bitwise for Performance & Flags
// Read (4: 100), Write (2: 010), Execute (1: 001)
const READ = 4, WRITE = 2, EXEC = 1;

let myPerms = READ | WRITE; // 6 (110)
const canWrite = (myPerms & WRITE) === WRITE; // true

// Fast Floor (Bitwise double NOT)
console.log(~~4.9); // 4 (Faster than Math.floor for positive numbers)`;

    const precedenceCode = `// The Precedence Table in Action
console.log(2 + 3 * 4);   // 14 (* higher than +)
console.log((2 + 3) * 4); // 20 (Group highest)

// Right-to-Left Associativity for Exponentiation
console.log(2 ** 3 ** 2); // 512 (evaluates 3^2 first, then 2^9)`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">JavaScript Fundamentals</div>
                <h1>Operators & Expressions</h1>
                <p className="hero-lead">
                    Master the tools of logic and computation. From basic arithmetic to advanced
                    bitwise flags and logical assignments, understand how to manipulate data with precision.
                </p>
            </div>

            <div className="content-section">
                <h2>Arithmetic & Assignment</h2>
                <p>
                    Arithmetic operators perform mathematical calculations, while assignment operators
                    store the results. Modern JavaScript includes the <strong>Exponentiation</strong>
                    operator and <strong>Logical Assignment</strong>.
                </p>
                <CodeBlock language="javascript" code={basicOpCode} />
            </div>

            <div className="content-section">
                <h2>Comparison: The Strictness Standard</h2>
                <p>
                    Comparing values is fundamental to logic. In professional environments,
                    <strong>Strict Equality (<code>===</code>)</strong> is the non-negotiable standard
                    to avoid the "Silent Coercion" bugs associated with <code>==</code>.
                </p>
                <CodeBlock language="javascript" code={comparisonCode} />
                <ProTip><strong> Pro Tip:</strong> Lexicographical comparison (string comparison) compares
                    character codes one by one. This is why <code>'2' &gt; '10'</code> is true; '2' has
                    a higher Unicode value than '1'.</ProTip>
            </div>

            <div className="content-section">
                <h2>Logical Operators & Short-Circuiting</h2>
                <p>
                    JavaScript engines use <strong>Short-Circuit evaluation</strong>. This means they
                    stop evaluating a boolean expression as soon as the result is determined. This
                    is widely used for safety checks and default values.
                </p>
                <CodeBlock language="javascript" code={logicalShortCircuitCode} />
            </div>

            <div className="content-section">
                <h2>The Nullish Coalescing Operator (<code>??</code>)</h2>
                <p>
                    Released in ES2020, <code>??</code> specifically checks for <code>null</code> or
                    <code>undefined</code>. It is superior to <code>||</code> when dealing with
                    valid "falsy" values like <code>0</code> or empty strings <code>""</code>.
                </p>
                <CodeBlock language="javascript" code={nullishCode} />
            </div>

            <div className="content-section">
                <h2>High-Performance Bitwise Logic</h2>
                <p>
                    Bitwise operators treat their operands as a set of 32 bits (zeros and ones). While
                    less common in UI development, they are critical for <strong>Game Engines</strong>,
                    <strong>Cryptography</strong>, and <strong>Binary Protocol Parsing</strong>.
                </p>
                <CodeBlock language="javascript" code={bitwiseCode} />
            </div>

            <div className="content-section">
                <h2>Operator Precedence</h2>
                <p>
                    Precedence determines the order in which operators are parsed. Use the
                    "Associativity" rule to understand which operations happen first if precedence
                    is equal.
                </p>
                <CodeBlock language="javascript" code={precedenceCode} />
                <table className="comparison-table">
                    <thead>
                        <tr>
                            <th>Level</th>
                            <th>Operator Category</th>
                            <th>Associativity</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1 (Highest)</td>
                            <td>Grouping <code>()</code></td>
                            <td>N/A</td>
                        </tr>
                        <tr>
                            <td>12</td>
                            <td>Multiplication <code>* / %</code></td>
                            <td>Left-to-right</td>
                        </tr>
                        <tr>
                            <td>13</td>
                            <td>Addition <code>+ -</code></td>
                            <td>Left-to-right</td>
                        </tr>
                        <tr>
                            <td>16</td>
                            <td>Logical AND <code>&&</code></td>
                            <td>Left-to-right</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <KeyFacts><ul>
                    <li><strong>Prefer <code>===</code>:</strong> Eliminate coercion side-effects.</li>
                    <li><strong>Use <code>??</code> for Settings:</strong> Don't let 0 or "" trigger defaults incorrectly.</li>
                    <li><strong>Parentheses for Clarity:</strong> Even if you know precedence, use <code>()</code> to help other developers.</li>
                    <li><strong>Logical Assignment:</strong> Use <code>||=</code> and <code>??=</code> for cleaner state updates.</li>
                    <li><strong>Unary Plus casting:</strong> Use <code>+value</code> for quick numeric conversion.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSOperators;


